import { useState, useEffect } from "react";
import axios from "axios";
import { Cpu, Globe } from "lucide-react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { ReactionButtons } from "../dashboard/ReactionButtons";
import { ObservatoryLoader } from "../common/ObservatoryLoader";
import { IndustryBreakdown } from "../industry/IndustryBreakdown";
import { AICCIMethodologyLink } from "../dashboard/AICCIMethodology";
import { AICCIBanner } from "../dashboard/AICCIBanner";
import { usePrefetchedData } from "../common/DataPrefetch";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const ORIENT_COLORS = { care: "#34d399", utility: "#60a5fa", creative: "#a78bfa", extraction: "#f87171" };
const ORIENT_LABELS = { care: "Care-Centered", utility: "Utility-Centered", creative: "Creative", extraction: "Extraction" };
const FORM_COLORS = { wearable: "#34d399", robotics: "#60a5fa", hardware: "#a78bfa", embedded: "#f59e0b", hybrid: "#06b6d4" };

const StatCard = ({ value, label, sub, color }) => (
  <div className="bg-card border border-border rounded-xl p-4 text-center">
    <p className="text-2xl md:text-3xl font-black tabular-nums" style={{ color }}>{value}</p>
    <p className="text-[10px] text-muted-foreground mt-1">{label}</p>
    {sub && <p className="text-[10px] text-muted-foreground/60">{sub}</p>}
  </div>
);

export const HardwareDashboard = () => {
  const prefetched = usePrefetchedData();
  const [data, setData] = useState(null);
  const [aicci, setAicci] = useState(null);
  const [loading, setLoading] = useState(true);
  const [insightIdx, setInsightIdx] = useState(0);
  const [newDevices, setNewDevices] = useState(0);
  const [inclusion, setInclusion] = useState(null);
  const [sources, setSources] = useState(null);
  const [overview, setOverview] = useState(null);

  // Sync from prefetch store
  useEffect(() => {
    if (prefetched.hwStats && !data) { setData(prefetched.hwStats); setLoading(false); }
    if (prefetched.aicciDevice && !aicci) setAicci(prefetched.aicciDevice);
    if (prefetched.hwInclusion && !inclusion) setInclusion(prefetched.hwInclusion);
    if (prefetched.hwSources && !sources) setSources(prefetched.hwSources);
    if (prefetched.hwOverview && !overview) setOverview(prefetched.hwOverview);
    if (prefetched.hwNewThisWeek && !newDevices) setNewDevices(prefetched.hwNewThisWeek?.new_devices || 0);
  }, [prefetched, data, aicci, inclusion, sources, overview, newDevices]);

  // Fallback fetch — only if prefetch hasn't delivered
  useEffect(() => {
    const timer = setTimeout(() => {
      if (data) return; // prefetch already arrived
      const load = async () => {
        try {
          const [d] = await Promise.allSettled([
            axios.get(`${API}/galaxy/stats?entity_type=device`),
          ]);
          if (d.status === "fulfilled") setData(prev => prev || d.value.data);
        } catch {}
        setLoading(false);

        try {
          const [ai, ntw, inc, src, ov] = await Promise.allSettled([
            axios.get(`${API}/aicci?entity_type=device`),
            axios.get(`${API}/mirror/new-this-week`),
            axios.get(`${API}/galaxy/inclusion?entity_type=device`),
            axios.get(`${API}/galaxy/sources?entity_type=device`),
            axios.get(`${API}/anatomy/devices/overview`),
          ]);
          if (ai.status === "fulfilled") setAicci(prev => prev || ai.value.data);
          if (ntw.status === "fulfilled") setNewDevices(prev => prev || (ntw.value.data?.new_devices || 0));
          if (inc.status === "fulfilled") setInclusion(prev => prev || inc.value.data);
          if (src.status === "fulfilled") setSources(prev => prev || src.value.data);
          if (ov.status === "fulfilled") setOverview(prev => prev || ov.value.data);
        } catch {}
      };
      load();
    }, 1500);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Insight rotation
  useEffect(() => {
    if (!data) return;
    const t = setInterval(() => setInsightIdx(i => (i + 1) % 1000), 8000);
    return () => clearInterval(t);
  }, [data]);

  if (loading) return <ObservatoryLoader message="Scanning physical AI devices" />;
  if (!data) return null;

  const round = (n, d) => Number(Number(n).toFixed(d));
  const monetization = data.monetization || overview?.monetization || [];
  const governance = data.governance || overview?.governance || {};
  const notableDevices = data.notable_devices || overview?.notable_devices || [];
  const countryCount = data.num_countries || overview?.countries_count || data.countries?.length || 0;
  const orientData = Object.entries(data.orientation || {}).map(([k, v]) => ({ name: ORIENT_LABELS[k] || k, value: v, color: ORIENT_COLORS[k] }));
  const formData = Object.entries(data.form_factors || {}).map(([k, v]) => ({ name: k, value: v, color: FORM_COLORS[k] || "#64748b" }));
  const founderData = Object.entries(data.founders || {}).map(([k, v]) => ({ name: k.replace("_", " "), value: v }));

  const hwCarePct = data.total > 0 ? round((data.orientation?.care || 0) / data.total * 100, 1) : null;
  const swCarePct = (() => {
    const appGalaxy = prefetched.galaxyOverview?.galaxies?.find(g => g.entity_type === "application");
    return appGalaxy?.care_pct ?? prefetched.pulse?.care_pct ?? null;
  })();
  const swAicci = prefetched.aicciApp?.score ?? null;

  const insights = [
    hwCarePct != null && `What is humanity solving for in hardware? ${hwCarePct}% care-oriented${swCarePct ? `, ${(hwCarePct / swCarePct).toFixed(1)}x more than software` : ""}`,
    `${data.total?.toLocaleString()} devices across ${countryCount} countries. Translation earbuds, surgical robots, prosthetic limbs, care companions`,
    inclusion?.accessibility?.count != null && `${inclusion.accessibility.count} devices serve the blind, deaf, mobility-impaired, and elderly. When technology extends the human form, inclusion is not optional`,
    `${data.form_factors?.robotics || 0} robots, ${data.form_factors?.wearable || 0} wearables, ${data.form_factors?.hardware || 0} hardware devices. Physical AI is where values become tangible`,
    aicci?.score != null && `Device AICCI: ${aicci.score}/100${swAicci != null ? ` vs Software: ${swAicci}/100` : ""}. Hardware scores higher on conscious innovation`,
  ].filter(Boolean);
  const insightPos = insights.length ? insightIdx % insights.length : 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="hardware-dashboard">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl" style={{ background: "linear-gradient(135deg, #060a12 0%, #0c1a2e 50%, #0a1020 100%)", border: "1px solid rgba(96,165,250,0.12)" }}>
        <div className="relative z-10 px-5 md:px-8 py-8 md:py-10">
          <p className="text-[10px] font-bold text-blue-400/70 uppercase tracking-[0.25em] mb-3">Physical AI Observatory</p>
          <h1 className="text-xl md:text-3xl font-bold text-white leading-tight max-w-2xl mb-3">
            When AI leaves the screen and enters the physical world, what values does it carry?
          </h1>
          <p className="text-xs md:text-sm text-slate-400 leading-relaxed max-w-2xl mb-4">
            Tracking AI hardware, wearables, robotics, and embedded devices across {countryCount} countries. From care robots to surveillance cameras, mapping what we choose to embody.
          </p>
          <div className="flex flex-wrap gap-2.5">
            <button onClick={() => document.getElementById('hw-content')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-xs md:text-sm font-semibold rounded-lg transition-colors shadow-lg shadow-blue-500/20">
              Explore the Devices
            </button>
            <button onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'about' }))}
              className="px-4 py-2 bg-white/[0.05] hover:bg-white/[0.1] border border-white/[0.1] text-white text-xs md:text-sm font-medium rounded-lg transition-colors">
              Read the Manifesto
            </button>
            <button onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'mirror' }))}
              className="px-4 py-2 bg-white/[0.05] hover:bg-white/[0.1] border border-white/[0.1] text-white text-xs md:text-sm font-medium rounded-lg transition-colors">
              View Findings
            </button>
          </div>
          <p className="text-xs text-blue-400/50 italic mt-4">The Embodied. Amplifying humanity.</p>
        </div>
      </div>


      {/* New Devices This Week */}
      {newDevices > 0 && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-blue-500/[0.08] border border-blue-500/20 rounded-xl" data-testid="new-devices-week">
          <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
          <span className="text-xs font-semibold text-blue-400">{newDevices.toLocaleString()} new device{newDevices > 1 ? "s" : ""} added this week</span>
        </div>
      )}

      {/* AICCI Banner for Hardware */}
      <div id="hw-content">
      <AICCIBanner entityType="device" />
      </div>{/* close hw-content */}

      {/* Key insight banner */}
      <div className="bg-gradient-to-r from-blue-500/[0.06] to-cyan-500/[0.04] border border-blue-500/20 rounded-xl p-4 md:p-5">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-500/15 flex items-center justify-center flex-shrink-0">
            <Cpu className="w-4 h-4 text-blue-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white/90">{insights[insightPos]}</p>
            <div className="flex items-center gap-3 mt-1.5">
              <p className="text-xs text-white/50">Physical AI Insight {insightPos + 1}/{insights.length}</p>
              <ReactionButtons insightId={`hw-insight-${insightPos}`} />
            </div>
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard value={(data.total || 0).toLocaleString()} label="Devices Tracked" color="#60a5fa" />
        <StatCard value={`${hwCarePct}%`} label="Care-Oriented" sub={swCarePct != null ? `vs ${swCarePct}% software` : undefined} color="#34d399" />
        <StatCard value={countryCount} label="Countries" color="#a78bfa" />
        <StatCard value={`${data.founders ? round((data.founders.female_only || 0) / Math.max(Object.values(data.founders).reduce((a,b)=>a+b,0), 1) * 100, 1) : 0}%`} label="Female-Founded" color="#ec4899" />
      </div>

      {/* Charts row 1: Orientation + Form Factor */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-bold mb-3">Value Orientation</h3>
          <div style={{ height: 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={orientData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={35}
                  labelLine={false} fontSize={10}>
                  {orientData.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0a1628", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }} formatter={(v, name) => [`${v} devices`, name]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap justify-center gap-3 mt-2">
            {orientData.map((e, i) => (
              <span key={i} className="flex items-center gap-1.5 text-[10px] text-slate-400">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: e.color }} />
                {e.name} ({e.value})
              </span>
            ))}
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-bold mb-3">Form Factors</h3>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={formData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 10 }} stroke="rgba(255,255,255,0.1)" />
                <YAxis tick={{ fill: "#64748b", fontSize: 10 }} stroke="rgba(255,255,255,0.1)" />
                <Tooltip contentStyle={{ background: "#0a1628", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]} animationDuration={1000}>
                  {formData.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Charts row 2: Countries + Founders */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-bold mb-3">Where Hardware AI is Built</h3>
          <div className="space-y-2">
            {data.countries?.slice(0, 12).map((c, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground w-24 truncate">{c.country}</span>
                <div className="flex-1 h-2.5 bg-white/[0.04] rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-blue-400" style={{ width: `${(c.count / (data.countries[0]?.count || 1)) * 100}%`, opacity: 1 - i * 0.06 }} />
                </div>
                <span className="text-xs font-bold text-muted-foreground w-8 text-right">{c.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-bold mb-3">Who Builds Physical AI</h3>
          <div style={{ height: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={founderData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis type="number" tick={{ fill: "#64748b", fontSize: 10 }} stroke="rgba(255,255,255,0.1)" />
                <YAxis type="category" dataKey="name" tick={{ fill: "#94a3b8", fontSize: 10 }} width={70} stroke="rgba(255,255,255,0.1)" />
                <Tooltip contentStyle={{ background: "#0a1628", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }} />
                <Bar dataKey="value" fill="#60a5fa" radius={[0, 4, 4, 0]} animationDuration={1000} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Monetization + Governance */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-bold mb-3">Monetization Models</h3>
          <div style={{ height: 160 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={monetization.slice(0, 5)} dataKey="count" nameKey="model" cx="50%" cy="50%" outerRadius={60}
                  labelLine={false} fontSize={9}>
                  {monetization.slice(0, 5).map((_, i) => <Cell key={i} fill={Object.values(ORIENT_COLORS)[i % 4]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0a1628", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap justify-center gap-2 mt-2">
            {monetization.slice(0, 5).map((m, i) => (
              <span key={i} className="text-[10px] text-slate-400">
                <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: Object.values(ORIENT_COLORS)[i % 4] }} />
                {m.model} ({m.count})
              </span>
            ))}
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-bold mb-3">Governance Framing</h3>
          <div className="space-y-3 mt-4">
            {Object.entries(governance).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between">
                <span className="text-xs text-slate-400">{k.replace("_", " ")}</span>
                <span className="text-sm font-bold text-slate-300">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Inclusion & Accessibility */}
      {inclusion && (
        <div className="bg-gradient-to-r from-cyan-500/[0.04] to-violet-500/[0.04] border border-cyan-500/15 rounded-xl p-5">
          <h3 className="text-sm font-bold mb-4">Inclusion & Accessibility</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="bg-white/[0.03] rounded-xl p-3 text-center border border-white/[0.04]">
              <p className="text-xl font-black text-cyan-400">{inclusion.accessibility?.count}</p>
              <p className="text-[10px] text-slate-400 mt-1">Accessibility Devices</p>
              <p className="text-[10px] text-cyan-400/60">{inclusion.accessibility?.pct}% of hardware</p>
            </div>
            <div className="bg-white/[0.03] rounded-xl p-3 text-center border border-white/[0.04]">
              <p className="text-xl font-black text-violet-400">{inclusion.lgbtqia?.content_count}</p>
              <p className="text-[10px] text-slate-400 mt-1">LGBTQIA+ Focus</p>
              <p className="text-[10px] text-violet-400/60">{inclusion.lgbtqia?.content_pct}%</p>
            </div>
            <div className="bg-white/[0.03] rounded-xl p-3 text-center border border-white/[0.04]">
              <p className="text-xl font-black text-pink-400">{inclusion.female_founded?.count}</p>
              <p className="text-[10px] text-slate-400 mt-1">Female-Founded</p>
              <p className="text-[10px] text-pink-400/60">{inclusion.female_founded?.pct}%</p>
            </div>
            <div className="bg-white/[0.03] rounded-xl p-3 text-center border border-white/[0.04]">
              <p className="text-xl font-black text-emerald-400">{inclusion.comparison?.ratio}x</p>
              <p className="text-[10px] text-slate-400 mt-1">More Accessible</p>
              <p className="text-[10px] text-emerald-400/60">than software ({inclusion.comparison?.other_acc_pct}%)</p>
            </div>
          </div>
          <p className="text-xs text-slate-400 leading-relaxed">
            Physical AI is {inclusion.comparison?.ratio}x more accessibility-focused than software. {inclusion.accessibility?.count} devices serve the blind, deaf, mobility-impaired, elderly, and cognitively disabled. When technology extends the human form, inclusion is not optional.
          </p>
          {inclusion.accessibility?.notable?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-white/[0.04]">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Notable Accessibility Devices</p>
              <div className="flex flex-wrap gap-2">
                {inclusion.accessibility.notable.slice(0, 8).map((d, i) => (
                  <span key={i} className="text-[10px] px-2 py-1 rounded-lg bg-white/[0.03] border border-white/[0.04] text-slate-400">
                    {d.product_name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Industry Breakdown */}
      <IndustryBreakdown entityType="device" />

      {/* Notable devices */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="text-sm font-bold mb-3">Notable Physical AI Devices</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {notableDevices.slice(0, 16).map((d, i) => (
            <div key={i} className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-white/[0.03]">
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: ORIENT_COLORS[d.value_orientation] || "#64748b" }} />
              <span className="text-xs font-medium text-slate-300 truncate flex-1">{d.product_name}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded-md border border-white/[0.06] text-slate-500">{d.form_factor}</span>
              {d.country && <span className="text-[10px] text-muted-foreground">{d.country}</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Data Sources */}
      {sources && sources.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-bold mb-4">Data Sources</h3>
          <div className="space-y-2">
            {sources.map((s, i) => {
              const maxCount = sources[0]?.count || 1;
              const colors = ["#60a5fa", "#34d399", "#ef4444", "#a78bfa", "#f59e0b", "#06b6d4", "#ec4899", "#fb923c", "#6366f1"];
              return (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-[10px] text-slate-400 w-36 truncate flex-shrink-0">{s.source}</span>
                  <div className="flex-1 h-2.5 bg-white/[0.04] rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${(s.count / maxCount) * 100}%`, background: colors[i % colors.length] }} />
                  </div>
                  <span className="text-xs font-bold text-slate-400 w-10 text-right">{s.count.toLocaleString()}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Closing */}
      <div className="text-center py-6 rounded-2xl" style={{ background: "rgba(96,165,250,0.04)", border: "1px solid rgba(96,165,250,0.12)" }}>
        <p className="text-lg font-bold text-white max-w-lg mx-auto italic">"When AI becomes something you can hold, does it hold more humanity?"</p>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between text-[10px] text-slate-500">
        <span>{typeof window !== "undefined" ? window.location.origin.replace("https://", "") : ""}</span>
        <span>&copy; {new Date().getFullYear()} AI Orientation Observatory</span>
      </div>
    </div>
  );
};

export default HardwareDashboard;
